<script setup lang="ts">
	import {
		RadioGroup,
		RadioGroupLabel,
		RadioGroupOption,
	} from '@headlessui/vue'
	import { QuestionMarkCircleIcon } from '@heroicons/vue/20/solid'
	import { CheckIcon } from '@heroicons/vue/24/solid'
	import { ref } from 'vue'

	import AlertComponent from '@/components/AlertComponent.vue'
	import CoincidencesAlert from '@/components/CoincidencesAlert.vue'
	import HasPermission from '@/components/HasPermission.vue'
	import RoundedBox from '@/components/RoundedBox.vue'
	import StyledButton from '@/components/StyledButton.vue'
	import StyledCombobox from '@/components/StyledCombobox.vue'
	import StyledInput from '@/components/StyledInput.vue'

	import { useAuth } from '@/composables/useAuth'
	import useAuthUser from '@/composables/useAuthUser'

	import BorderedBox from '@/modules/companies/components/BorderedBox.vue'
	import FieldSet from '@/modules/companies/components/FieldSet.vue'
	import EditCompanyLogo from '@/modules/companies/components/edit/EditCompanyLogo.vue'
	import MarkupInput from '@/modules/companies/components/edit/MarkupInput.vue'
	import useEditGeneralTab from '@/modules/companies/composables/useEditGeneralTab'
	import { CompanyEditTab } from '@/modules/companies/interfaces/companyEditTab'

	interface Props extends CompanyEditTab {}
	const props = defineProps<Props>()

	const { user } = useAuthUser()

	const {
		loadingUsersInternal,
		setQueryInternal,
		usersInternal,
		// users,
		corporationForm,
		updateFields,
		corporations,
		searchQuery,
		isCreatingCorporation,
		isUpdating,
		confirmingCorporation,
		confirmingUpdate,
		companyCoincidences,
		handleUpdate,
		handleUpdateEvaluation,
		handleCreateCorporation,
		setCompanyQuery,
		setCorporationQuery,
		onCancelUpdate,
		handleCreateCorpo,
	} = useEditGeneralTab(props.company)

	const isOpen = ref(false)
	const isServiceChargeInfoOpen = ref(false)
</script>

<template>
	<RoundedBox class="bg-white">
		<section class="w-full grid gap-[1.563rem]">
			<h2 class="text-xl font-medium">Información general</h2>

			<div class="grid grid-cols-1 md:grid-cols-2 gap-5">
				<EditCompanyLogo
					:company="company"
					name-permission-update="Update companies"
					collection="logo"
					title="Logo"
					description="Gestiona el logo completo de la empresa"
					example-text="Ej. logo con el nombre de la empresa, usado en encabezados y documentos."
				/>
				<EditCompanyLogo
					:company="company"
					name-permission-update="Update companies"
					collection="isotipo"
					title="Isotipo"
					description="Gestiona el isotipo (símbolo) de la empresa"
					example-text="Ej. solo el ícono o símbolo de la marca, sin texto, usado en espacios reducidos."
				/>
			</div>

			<FieldSet>
				<StyledInput
					v-model="updateFields.name"
					label="Nombre de la empresa:"
					placeholder="ej. Enlaceforte"
					:on-model-change="(value) => setCompanyQuery(value)"
				/>

				<StyledCombobox
					:model-value="corporationForm"
					@update:model-value="
						(value: any) => {
							updateFields.corporation = value
							corporationForm.name = value.name
							setCorporationQuery(value)
						}
					"
					:options="corporations"
					:display-value="(option) => option.name"
					:on-input-change="
						(value) => {
							corporationForm.name = value
							setCorporationQuery(value)
						}
					"
					:placeholder="company.corporationName || 'Seleccione un corporativo'"
					label="Corporativo:"
					:reset-query-after-select="false"
				>
					<template #customOption>
						{{ corporationForm.name }}
						<StyledButton
							type="button"
							:disabled="isCreatingCorporation"
							class="border bg-[#253C70] text-white mx-auto w-full justify-center"
							:loading="isCreatingCorporation"
							@click.prevent="
								() => {
									handleCreateCorporation()
								}
							"
						>
							Crear corporativo
						</StyledButton>
					</template>

					<template v-slot="{ option }">
						<span>{{ option.name }}</span>
					</template>
				</StyledCombobox>

				<StyledCombobox
					enableFuse
					v-model="updateFields.executive"
					:options="usersInternal || []"
					:display-value="
						(option) => option && `${option.name} ${option.lastname}`
					"
					:placeholder="
						props.company.executiveName || 'Seleccione un ejecutivo'
					"
					:fuse-keys="['fullName']"
					label="Ejecutivo"
					:on-input-change="setQueryInternal"
					:loading-options="loadingUsersInternal"
				>
					<template v-slot="{ option }">
						<span>{{ option.name }} {{ option.lastname }}</span>
					</template>
				</StyledCombobox>

				<StyledInput
					:model-value="updateFields.amadeusRatePlanCode"
					@update:model-value="(val: string) => updateFields.amadeusRatePlanCode = val?.toUpperCase()"
					label="Rate Code:"
					placeholder="ENF"
					input-class="uppercase"
				>
					<template #labelCompnent>
						<span
							:class="[
								'w-fit rounded-lg bg-gray-300 bg-opacity-20 p-1 text-sm font-medium text-white transition-colors duration-500 hover:bg-opacity-30 cursor-pointer',
								,
							]"
							@click.prevent="isOpen = true"
						>
							<QuestionMarkCircleIcon
								:class="[
									'h-5 w-5 text-gray-400 transition-colors duration-500',
								]"
								aria-hidden="true"
							/>
						</span>
					</template>
				</StyledInput>

				<!-- Campo mejorado para cargo por plataforma -->
				<div class="flex flex-col gap-2">
					<StyledInput
						v-model="updateFields.platformServiceCharge"
						label="Cargo por uso de plataforma (MXN):"
						placeholder="ej. 150.00"
						type="number"
						:min="0"
						:max="10000"
						step="0.01"
					>
						<template #labelCompnent>
							<span
								:class="[
									'w-fit rounded-lg bg-gray-300 bg-opacity-20 p-1 text-sm font-medium text-white transition-colors duration-500 hover:bg-opacity-30 cursor-pointer',
									,
								]"
								@click.prevent="isServiceChargeInfoOpen = true"
							>
								<QuestionMarkCircleIcon
									:class="[
										'h-5 w-5 text-gray-400 transition-colors duration-500',
									]"
									aria-hidden="true"
								/>
							</span>
						</template>
					</StyledInput>

					<!-- Campos de checkbox por cobro de estacia y el tipo de cobro de estancia -->
					<div class="flex flex-col gap-3">
						<RadioGroup v-model="updateFields.platformServiceChargeType">
							<RadioGroupLabel>
								¿Cobro por porcentaje o monto?
							</RadioGroupLabel>

							<div class="flex justify-between w-full gap-2">
								<RadioGroupOption
									v-for="option in ['percentage', 'amount']"
									:key="option.toString()"
									v-slot="{ checked }"
									:value="option"
									class="flex-1"
								>
									<li
										:class="{
											'bg-blue-500 text-white': checked,
											'bg-slate-200 text-black': !checked,
										}"
										class="flex items-center gap-2 p-2 rounded-md cursor-pointe hover:text-white hover:bg-blue-300 hover:shadow-md transition-all"
									>
										{{ option === 'percentage' ? 'Porcentaje' : 'Monto' }}

										<CheckIcon
											v-show="checked"
											class="w-4"
										/>
									</li>
								</RadioGroupOption>
							</div>
						</RadioGroup>

						<RadioGroup v-model="updateFields.platformServiceChargePerNight">
							<RadioGroupLabel> ¿Cobro por noche? </RadioGroupLabel>
							<div class="flex justify-between w-full gap-2">
								<RadioGroupOption
									v-for="(option, i) in [false, true]"
									:key="i"
									v-slot="{ checked }"
									:value="option"
									class="flex-1"
								>
									<li
										:class="{
											'bg-blue-500 text-white': checked,
											'bg-slate-200 text-black': !checked,
										}"
										class="flex items-center gap-2 p-2 rounded-md cursor-pointe hover:text-white hover:bg-blue-300 hover:shadow-md transition-all"
									>
										{{ option ? 'Noche' : 'Estancia' }}

										<CheckIcon
											v-show="checked"
											class="w-4"
										/>
									</li>
								</RadioGroupOption>
							</div>
						</RadioGroup>
					</div>
				</div>
			</FieldSet>

			<StyledButton
				label="Guardar"
				class="bg-[#253C70] text-white ml-auto"
				:disabled="isUpdating"
				:loading="isUpdating"
				@click="handleUpdateEvaluation"
			/>
			.
		</section>

		<CoincidencesAlert
			v-model:confirming="confirmingCorporation"
			:coincidences="corporations"
			title="Por favor, confirme la creación del corporativo:"
			create-button-text="Creax|r corporativo"
			:on-cancel="() => (isCreatingCorporation = false)"
			:on-confirm="handleCreateCorpo"
		>
			<template #coincidence="{ coincidence }">
				{{ coincidence.name }}
			</template>
		</CoincidencesAlert>

		<CoincidencesAlert
			v-model:confirming="confirmingUpdate"
			:coincidences="companyCoincidences"
			title="Por favor, confirme el nuevo nombre de la empresa:"
			body="Se encontraron algunas coincidencias con el nombre similar al que desea cambiar."
			create-button-text="Cambiar nombre"
			:on-cancel="onCancelUpdate"
			:on-confirm="handleUpdate"
		>
			<template #coincidence="{ coincidence }">
				{{ coincidence.name }}
			</template>
		</CoincidencesAlert>
	</RoundedBox>

	<AlertComponent
		:dismiss="true"
		v-model="isOpen"
		type="info"
	>
		<template #title> Aviso</template>
		<template #body>
			<p class="text-justify text-sm">
				Si la empresa cuenta con un convenio corporativo con hoteles, favor de
				carga tu codigo para GDS amadeus
			</p>
		</template>
		<template #closeButton>Entendido</template>
	</AlertComponent>

	<!-- Nuevo componente de alerta para el cargo por servicio -->
	<AlertComponent
		:dismiss="true"
		v-model="isServiceChargeInfoOpen"
		type="info"
	>
		<template #title>Cargo por uso de plataforma</template>
		<template #body>
			<p class="text-justify text-sm">
				Este monto en pesos mexicanos (MXN) se aplicará como cargo adicional por
				el uso de la plataforma cuando la empresa utilice tarifas privadas con
				su Rate Code empresarial. El cargo se añadirá al precio total de cada
				reservación.
			</p>
		</template>
		<template #closeButton>Entendido</template>
	</AlertComponent>
</template>

<style scoped></style>
